import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Search, Instagram, Twitter, Youtube, Check } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from "recharts";
import { SITE_URL } from "@/lib/utils";
import { Reveal } from "@/components/reveal";
import Waves from "@/components/Waves";
import { FlipWords } from "@/components/flip-words";
import { PhoneMockup } from "@/components/phone-mockup";
import { ProductSkeleton as PSkeleton } from "@/components/skeleton-card";

export const Route = createFileRoute("/demo")({
  head: () => ({
    meta: [
      { title: "See Pluggz in action — Interactive Demo" },
      { name: "description", content: "Take a self-guided tour of the Pluggz creator affiliate platform. No signup required." },
      { property: "og:title", content: "See Pluggz in action — Interactive Demo" },
      { property: "og:description", content: "Take a self-guided tour of the Pluggz creator affiliate platform. No signup required." },
      { property: "og:url", content: `${SITE_URL}/demo` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/demo` }],
  }),
  component: DemoPage,
});

const STEPS = [
  { id: "homepage", label: "Homepage" },
  { id: "discover", label: "Discover" },
  { id: "creator", label: "Creator Storefront" },
  { id: "dashboard", label: "Dashboard" },
  { id: "admin", label: "Admin" },
] as const;

const CAPTIONS = [
  "The homepage showcases trending products and featured creator stories",
  "Shoppers discover products by category, brand or creator — powered by Algolia search",
  "Every creator gets their own branded storefront with curated products, collections and shoppable looks",
  "Creators track clicks, commissions and earnings in real time from their dashboard",
  "Lisa manages creator approvals, commissions, affiliate syncs and platform analytics from the admin panel",
];

function DemoPage() {
  const [step, setStep] = useState(0);
  const [auto, setAuto] = useState(false);

  const next = () => setStep((s) => (s + 1) % STEPS.length);
  const prev = () => setStep((s) => (s - 1 + STEPS.length) % STEPS.length);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => setStep((s) => (s + 1) % STEPS.length), 4000);
    return () => clearInterval(id);
  }, [auto]);

  return (
    <div className="min-h-dvh">
      {/* Minimal top bar */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" aria-label="Pluggz home" className="font-serif text-xl tracking-tight">Pluggz.</Link>
          <Link
            to="/apply"
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background hover:bg-foreground/90 transition"
          >
            Get started
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-12 sm:pt-16">
        <Reveal>
          <p className="editorial-eyebrow text-center">Interactive demo</p>
          <h1 className="mt-2 text-center font-serif text-4xl sm:text-5xl md:text-6xl">Explore Pluggz in 2 minutes</h1>
          <p className="mt-4 text-center text-sm text-muted-foreground">Click through the platform at your own pace. No signup needed.</p>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStep(i)}
                className={
                  "rounded-full px-4 py-2 text-xs uppercase tracking-widest transition " +
                  (i === step
                    ? "bg-foreground text-background"
                    : "border border-border text-muted-foreground hover:text-foreground")
                }
              >
                {s.label}
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Demo container */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-10">
        <div className="relative border border-border rounded-xl overflow-hidden shadow-sm bg-background">
          <div key={step} className="animate-fade-in">
            {step === 0 && <HomepageDemo />}
            {step === 1 && <DiscoverDemo />}
            {step === 2 && <CreatorDemo />}
            {step === 3 && <DashboardDemo />}
            {step === 4 && <AdminDemo />}
          </div>
        </div>
        <p className="mt-4 text-center text-sm text-muted-foreground">{CAPTIONS[step]}</p>

        {/* Navigation */}
        <div className="mt-8 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={prev}
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-accent transition"
          >
            <ArrowLeft className="h-4 w-4" /> Previous
          </button>
          <div className="flex flex-col items-center gap-2">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Step {step + 1} of {STEPS.length}</p>
            <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => setAuto(e.target.checked)}
                className="h-3 w-3 accent-foreground"
              />
              Auto-play
            </label>
          </div>
          <button
            type="button"
            onClick={next}
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-accent transition"
          >
            Next <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      {/* CTA cards */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-20 sm:py-28">
        <div className="grid gap-4 sm:grid-cols-3">
          <CtaCard title="For Creators" body="Apply to join Pluggz and start earning" to="/apply" />
          <CtaCard title="For Brands" body="Feature your products with our creators" to="/advertise" />
          <CtaCard title="For Shoppers" body="Discover curated products" to="/discover" />
        </div>
      </section>
    </div>
  );
}

function CtaCard({ title, body, to }: { title: string; body: string; to: string }) {
  return (
    <Link
      to={to}
      className="group block border border-border rounded-xl p-6 hover:bg-accent transition"
    >
      <p className="editorial-eyebrow">{title}</p>
      <p className="mt-3 font-serif text-xl leading-snug">{body}</p>
      <span className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground group-hover:text-foreground transition">
        Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

/* --------------------------------- STEP 1 --------------------------------- */
function HomepageDemo() {
  return (
    <div className="bg-background">
      <div className="relative overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-12 min-h-[420px]">
          <div className="relative order-1 px-6 py-10 md:col-span-7 md:px-12 md:py-14">
            <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.3]">
              <Waves lineColor="rgba(139,115,85,0.55)" backgroundColor="transparent" waveSpeedY={0.01} waveAmpX={40} waveAmpY={20} xGap={12} yGap={36} />
            </div>
            <div className="relative">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
                The things our{" "}
                <FlipWords words={["favourite people", "creators", "editors"]} className="italic" duration={2800} />
                <br />actually use.
              </h2>
              <p className="mt-4 max-w-md text-sm text-muted-foreground">
                A curated marketplace of creator-led storefronts.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 border-b border-foreground pb-1 text-sm">
                Enter the discovery feed <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </div>
          <div className="relative order-2 px-6 py-8 md:col-span-5 md:px-8 md:py-10 min-h-[340px]">
            <PhoneMockup products={[]} />
          </div>
        </div>
      </div>
      <div className="border-t border-border px-6 py-8 md:px-12">
        <p className="editorial-eyebrow">Trending products</p>
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <PSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- STEP 2 --------------------------------- */
function DiscoverDemo() {
  const cats = ["Beauty", "Fashion", "Home", "Travel", "Technology"];
  return (
    <div className="bg-background px-6 py-10 md:px-12">
      <div className="relative flex items-center gap-3 rounded-md border border-border bg-card px-4 py-3">
        <Search className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Search products, creators, brands…</span>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {cats.map((c, i) => (
          <span
            key={c}
            className={
              "rounded-full px-3 py-1.5 text-xs " +
              (i === 0 ? "bg-foreground text-background" : "border border-border text-muted-foreground")
            }
          >
            {c}
          </span>
        ))}
      </div>
      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <PSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/* --------------------------------- STEP 3 --------------------------------- */
function CreatorDemo() {
  return (
    <div className="bg-background">
      <div className="border-b border-border px-6 py-10 md:px-12 text-center">
        <div className="mx-auto h-24 w-24 rounded-full bg-muted ring-1 ring-border" aria-hidden />
        <h3 className="mt-4 font-serif text-3xl">Sophie K.</h3>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">@sophiek</p>
        <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
          Curating the best in contemporary fashion and beauty
        </p>
        <div className="mt-4 flex justify-center gap-4 text-muted-foreground">
          <Instagram className="h-4 w-4" />
          <Twitter className="h-4 w-4" />
          <Youtube className="h-4 w-4" />
        </div>
      </div>
      <div className="flex gap-6 border-b border-border px-6 md:px-12">
        <button className="border-b-2 border-foreground py-3 text-sm font-medium">Products</button>
        <button className="py-3 text-sm text-muted-foreground">Collections</button>
        <button className="py-3 text-sm text-muted-foreground">Looks</button>
      </div>
      <div className="px-6 py-8 md:px-12">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <PSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- STEP 4 --------------------------------- */
function DashboardDemo() {
  const stats = [
    { v: "£2,840", l: "Total earned" },
    { v: "143", l: "Link clicks" },
    { v: "£340", l: "Pending payout" },
    { v: "12", l: "Products curated" },
  ];
  const chartData = [
    { m: "Jan", v: 120 },
    { m: "Feb", v: 280 },
    { m: "Mar", v: 190 },
    { m: "Apr", v: 420 },
    { m: "May", v: 380 },
    { m: "Jun", v: 340 },
  ];
  return (
    <div className="bg-background px-6 py-10 md:px-12">
      <p className="editorial-eyebrow">Creator dashboard</p>
      <h3 className="mt-2 font-serif text-2xl">Welcome back, Sophie</h3>
      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        {stats.map((s) => (
          <div key={s.l} className="border border-border rounded-md p-4">
            <p className="font-serif text-2xl">{s.v}</p>
            <p className="mt-1 text-xs text-muted-foreground">{s.l}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 border border-border rounded-md p-4">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Earnings · last 6 months</p>
        <div className="mt-4 h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="m" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "currentColor" }} />
              <Bar dataKey="v" radius={[4, 4, 0, 0]}>
                {chartData.map((_, i) => (
                  <Cell key={i} fill="var(--foreground)" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- STEP 5 --------------------------------- */
function AdminDemo() {
  const stats = [
    { v: "£12,450", l: "Platform revenue" },
    { v: "48", l: "Active creators" },
    { v: "2,341", l: "Monthly clicks" },
    { v: "4.8%", l: "Avg conversion" },
  ];
  const rows = [
    { name: "Sophie K.", status: "Live", earned: "£2,840" },
    { name: "Marcus T.", status: "Pending", earned: "£0" },
    { name: "Ada R.", status: "Live", earned: "£1,210" },
  ];
  return (
    <div className="bg-background px-6 py-10 md:px-12">
      <p className="editorial-eyebrow">Admin overview</p>
      <h3 className="mt-2 font-serif text-2xl">Lisa's control panel</h3>
      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        {stats.map((s) => (
          <div key={s.l} className="border border-border rounded-md p-4">
            <p className="font-serif text-2xl">{s.v}</p>
            <p className="mt-1 text-xs text-muted-foreground">{s.l}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-normal">Creator</th>
              <th className="px-4 py-3 text-left font-normal">Status</th>
              <th className="px-4 py-3 text-left font-normal">Commissions</th>
              <th className="px-4 py-3 text-right font-normal">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-border">
                <td className="px-4 py-3">{r.name}</td>
                <td className="px-4 py-3">
                  <span
                    className={
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest " +
                      (r.status === "Live"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                        : "bg-muted text-muted-foreground")
                    }
                  >
                    {r.status === "Live" && <Check className="h-2.5 w-2.5" />}
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 font-serif">{r.earned}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    className="rounded-md border border-border px-3 py-1 text-xs hover:bg-accent transition"
                  >
                    Approve
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}